import { useState } from 'react';
import Filters from '../Table/Filters';
import SharedButton from '../../global/Button/Button';
import { getFilterLabel } from '../../modules/search/searchGrid/helpers/getFiltersLabels';
import SavedFiltersModal from './SavedFiltersModal';
import { userAppContext } from '../../context/Context';
import { UserContext } from '../../types';
import Alert from '../../global/Alert/Alert';
import { usePrivacyPolicy } from '../../hooks/usePrivacyPolicy';
import PrivacyPolicyModal from '../Common/PrivacyPolicyModal';
import '../../modules/search/searchGrid/components/table/SummaryMatchBar.css';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';

const ActiveFiltersBar = ({
  filters,
  onRemoveFilter,
  onRemoveFilterValue,
  onClearAll,
  isEvidence = false,
  showSummaryMatchBar = false,
  onOpenSummaryMatch,
  isSummaryMatchLoading = false,
  summaryMatchActive = false,
  summaryMatchStats = null,
  summaryMatchError = null,
  onClearSummaryMatch,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const { isAuthenticated } = userAppContext() as unknown as UserContext;
  const { markAsAccepted } = usePrivacyPolicy();

  const showSummaryTableButton = process.env.REACT_APP_SHOW_SUMMARY_TABLE_BUTTON === 'true';

  const activeFilters = filters.filter(
    f =>
      f.value !== undefined &&
      f.value !== null &&
      f.value !== '' &&
      !(Array.isArray(f.value) && f.value.length === 0),
  );

  function getLabelsForActiveFilters(activeFilters, filters) {
    return activeFilters.map(activeFilter => {
      const matchingFilter = filters.find(filter => filter.name === activeFilter.name);
      return {
        ...activeFilter,
        label: matchingFilter ? matchingFilter.label : 'Unknown',
      };
    });
  }

  const activeFiltersWithLabels = getLabelsForActiveFilters(activeFilters, Filters);
  const normalizedSearchTerm = filterSearchTerm.trim().toLowerCase();

  const matchesSearchTerm = (text: string) =>
    !normalizedSearchTerm || text.toLowerCase().includes(normalizedSearchTerm);

  const handleApplyFilter = savedFilters => {
    onClearAll();

    if ((window as any).gridRef?.current) {
      (window as any).gridRef.current.setFilterValue(savedFilters);
    }
  };

  const handleSaveSuccess = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  const handlePrivacyPolicyAccept = () => {
    markAsAccepted();
    setShowPrivacyPolicyModal(false);
    setIsModalOpen(true);
  };

  const handlePrivacyPolicyClose = () => {
    setShowPrivacyPolicyModal(false);
  };

  if (activeFilters.length === 0 && !summaryMatchActive) return null;

  const statsLine =
    summaryMatchStats &&
    `Summary filtered to ${summaryMatchStats.matchFilterCount} ${summaryMatchStats.matchFilterLabel} from filtered Evidence (${summaryMatchStats.uniqueAssociationCount} association${summaryMatchStats.uniqueAssociationCount !== 1 ? 's' : ''}, ${summaryMatchStats.evidenceRowCount} Evidence row${summaryMatchStats.evidenceRowCount !== 1 ? 's' : ''}). You can apply additional Summary filters below.`;

  return (
    <>
      {showSuccessAlert && <Alert message={successMessage} type="success" />}

      {summaryMatchActive && !isEvidence && (statsLine || summaryMatchError) && (
        <div className={`summary-match-bar mr-8${summaryMatchError ? ' summary-match-bar--error' : ''}`}>
          <span className="summary-match-bar-text">{summaryMatchError || statsLine}</span>
          {onClearSummaryMatch && (
            <SharedButton variant="secondaryText" size="xs" onClick={onClearSummaryMatch} className="mb-0">
              Clear Summary match
            </SharedButton>
          )}
        </div>
      )}

      {activeFilters.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 mr-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Active Filters ({activeFilters.length})</span>
            <div className="flex gap-2 items-center justify-end">
              {showSummaryMatchBar && onOpenSummaryMatch && showSummaryTableButton && (
                <>
                  <BaseTooltip content="View which Summary associations match these results." position="top">
                    <SharedButton
                      variant="secondaryText"
                      size="xs"
                      onClick={onOpenSummaryMatch}
                      disabled={isSummaryMatchLoading}
                      style={{ minWidth: 'max-content' }}
                    >
                      {isSummaryMatchLoading ? 'Building match…' : 'View Summary match'}
                    </SharedButton>
                  </BaseTooltip>
                </>
              )}
              <input
                type="text"
                value={filterSearchTerm}
                onChange={e => setFilterSearchTerm(e.target.value)}
                placeholder="Search applied filters..."
                className="filter-input"
                style={{ minWidth: '220px' }}
              />
              <div style={{ maxWidth: 'max-content', flexShrink: 0 }}>
                <SharedButton variant="secondary" size="xs" onClick={onClearAll} className="my-0">
                  Clear all
                </SharedButton>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto min-h-0 overscroll-contain" id="tags">
            {activeFiltersWithLabels.map((filter, index) =>
              Array.isArray(filter.value) && filter.value.length > 0 && onRemoveFilterValue
                ? filter.value
                    .filter(itemValue => matchesSearchTerm(`${filter.label}: ${String(itemValue)}`))
                    .map((itemValue, i) => (
                      <div
                        key={`${filter.name}-${String(itemValue)}-${i}`}
                        className="etiquetas etpeq w-auto"
                      >
                        <span
                          className="text-gray-700 ml-0"
                          onClick={() => onRemoveFilterValue(filter.name, String(itemValue))}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRemoveFilterValue(filter.name, String(itemValue));
                            }
                          }}
                        >
                          {filter.label}: {String(itemValue)}
                        </span>
                      </div>
                    ))
                : matchesSearchTerm(getFilterLabel(filter)) && (
                    <div
                      key={`${filter.name}-${filter.operator || ''}-${index}`}
                      className="etiquetas etpeq w-auto"
                    >
                      <span className="text-gray-700 ml-0" onClick={() => onRemoveFilter(filter)}>
                        {getFilterLabel(filter)}
                      </span>
                    </div>
                  ),
            )}
          </div>
        </div>
      )}

      {isAuthenticated && (
        <>
          <SavedFiltersModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onApplyFilter={handleApplyFilter}
            currentFilters={filters}
            isEvidence={isEvidence}
            onSaveSuccess={handleSaveSuccess}
          />

          <PrivacyPolicyModal
            isOpen={showPrivacyPolicyModal}
            onClose={handlePrivacyPolicyClose}
            onAccept={handlePrivacyPolicyAccept}
          />
        </>
      )}
    </>
  );
};

export default ActiveFiltersBar;
